import { WorkItemStateEnum, WorkItemTypeEnum } from "./work.types";

// Sales manager-related interfaces
// Breaking change: api/v2/crm/sales-manager bygger nu på ett typat
// breakdown-träd (SegmentBreakdown → CategoryBreakdown → SubtypeBreakdown →
// ActivityCounts). Alla tre endpoints levererar samma byggsten.

// ---------- Enums ----------

// Kategori för en säljaktivitet (ny i v2)
export enum WorkItemCategory {
  Communication = 1,
  Followup = 2,
}

// Subtyp inom en kategori (ny i v2)
export enum WorkItemSubtype {
  Email = 1,
  PhoneCall = 2,
  Sms = 3,
  DigitalMeeting = 4,
  PhysicalMeeting = 5,
  Other = 6, // Övriga säljaktiviteter som inte passar i de fem huvudtyperna
  Admin = 7, // Interna admin-uppgifter (Planering-kategorin)
  ContactAttempt = 8, // Kontaktförsök — effort-metrik, ingår EJ i Total
}

// Kundsegment baserat på CustomerJourneyRelationType — finkornig vy med 8
// buckets. De tidigare coarse-värdena (ExistingCustomer/SuspectProspect/Other)
// är borttagna — frontend får summera själv om en grövre vy behövs.
export enum CustomerSegment {
  SuspectProspect = 1, // 11-24 (Lead, Suspect, Prospect, HotProspect, Vilande)
  Pipeline = 2, // 31, 41 (KundFullmakt, KundIRöret)
  ExistingCustomer = 3, // 51-61
  CrossSellOpportunity = 4, // 62-69 (KundMedAktivAnnanTjänst, KundMedAnnansAvtal, KundÖvrigt)
  Churning = 5, // 71 (KundLämnar)
  FormerCustomer = 6, // 81-85, 92, 99 (FöreDetta*, Arkiverad, Gallrad)
  NetworkOnly = 7, // 91, 93 (EndastNät, FöreDettaKundNät)
  Unknown = 8, // null — ingen klassificering
}

// ---------- Breakdown-träd ----------

// Lägsta nivån: rena siffror för en cell i matrisen
export interface ActivityCounts {
  Total: number;
  Completed: number; // WorkItemState == Closed
  Skipped: number; // delmängd av Completed — markerade som Skipped i WorkItemResult
  Active: number; // WorkItemState == Active
  Overdue: number; // Active + ScheduledTo < Now (snapshot, oberoende av period)
  CompletionRate: number; // procent 0-100, 1 decimal; (Completed - Skipped) / (Total - Skipped)
}

// Per subtyp inom en kategori
export interface SubtypeBreakdown {
  Email: ActivityCounts;
  PhoneCall: ActivityCounts;
  Sms: ActivityCounts;
  DigitalMeeting: ActivityCounts;
  PhysicalMeeting: ActivityCounts;
  Admin: ActivityCounts;
  Other: ActivityCounts;
  // Effort-metrik: räknas EJ in i Total. Visa endast antal (Total-fältet),
  // inte completion rate — kontaktförsök skapas alltid stängda.
  ContactAttempt: ActivityCounts;
  Total: ActivityCounts; // summan av subtyperna UTOM ContactAttempt
}

// Per kategori inom ett segment
export interface CategoryBreakdown {
  Communication: SubtypeBreakdown; // Email, PhoneCall, Sms, DigitalMeeting, PhysicalMeeting
  Followup: SubtypeBreakdown; // FollowUp*-motsvarigheterna
  Total: SubtypeBreakdown; // communication + followup
}

// Toppnivå: 8 finkorniga segment + total + metadata om schemalagd/backlog.
// Tidigare coarse-buckets (ExistingCustomer/SuspectProspect/Other) är
// borttagna och ersatta med de 8 nya direkt på toppen.
export interface SegmentBreakdown {
  SuspectProspect: CategoryBreakdown; // 11-24
  Pipeline: CategoryBreakdown; // 31, 41
  ExistingCustomer: CategoryBreakdown; // 51-61
  CrossSellOpportunity: CategoryBreakdown; // 62-69
  Churning: CategoryBreakdown; // 71
  FormerCustomer: CategoryBreakdown; // 81-85, 92, 99
  NetworkOnly: CategoryBreakdown; // 91, 93
  Unknown: CategoryBreakdown; // null — ingen klassificering
  Total: CategoryBreakdown; // summan av alla 8

  UnscheduledCount: number; // items utan ScheduledTo, ingår inte i matrisen
  CarriedOverBacklog: number; // Active + ScheduledTo < fromDate, "skuld" in i perioden
}

// Nyckel för ett segment i SegmentBreakdown (exkl. Total).
export type SegmentKey =
  | "SuspectProspect"
  | "Pipeline"
  | "ExistingCustomer"
  | "CrossSellOpportunity"
  | "Churning"
  | "FormerCustomer"
  | "NetworkOnly"
  | "Unknown";

// ---------- Responsetyper ----------

// Lättviktsvy av ett work item i sales-manager-svar
export interface WorkItemSummary {
  Id: number;
  Name: string;
  Type: WorkItemTypeEnum; // rå enum
  State: WorkItemStateEnum;
  ScheduledTo: string | null;
  Created: string;
  Modified: string;
  CustomerName: string | null;
  TeamName: string | null;
  Category: WorkItemCategory | null; // null om typen inte är en säljaktivitet
  Subtype: WorkItemSubtype | null;
  Segment: CustomerSegment;
}

// Utmärkelse — ersätter den gamla completion-rate-topplistan.
export interface Award {
  Key: string; // stabil id (bulldog, fast, marathon, ...) — använd, inte titeln
  Title: string;
  Description: string;
  SalespersonId: number | null; // null = ingen vinnare än
  SalespersonName: string | null;
  Value: number; // råa talet
  ValueLabel: string; // färdigformaterad — visa rakt av
}

// "Fun facts"-band — aggregerade siffror över valda säljare/period.
export interface FunFacts {
  TotalTouches: number; // aktiviteter exkl. kontaktförsök
  TotalContactAttempts: number;
  TotalCompleted: number;
  UniqueCustomers: number;
  BusiestDay: string | null; // YYYY-MM-DD, kan vara null
  BusiestDayCount: number;
}

// Daglig serie för trend-grafen
export interface ActivityTrend {
  Date: string; // YYYY-MM-DD
  Scheduled: number; // items med ScheduledTo på dagen
  Completed: number; // items stängda (Changed) på dagen
}

// Stats per säljare
export interface SalespersonActivityStats {
  SalespersonId: number;
  SalespersonName: string;
  SalespersonImagePath: string;
  Breakdown: SegmentBreakdown;
  AverageCompletionTimeDays: number | null;
  RecentActivities: WorkItemSummary[]; // 10 senaste
  TeamId: number | null;
  TeamName: string | null;
}

// Team-överblick (aggregerad över valda säljare)
export interface SalesTeamSummary {
  TotalSalespeople: number;
  Breakdown: SegmentBreakdown;
  Awards: Award[]; // 5 utmärkelser (ersätter TopPerformers)
  FunFacts: FunFacts;
  ActivityTrends: ActivityTrend[];
  PeriodStart: string; // ISO
  PeriodEnd: string | null; // ISO
}

// Overview / KPI-snapshot
export interface SalesOverviewStats {
  TotalSalespeople: number;
  Breakdown: SegmentBreakdown;
  CompletedToday: number; // Closed && Changed.Date == Today
  Awards: Award[]; // 5 utmärkelser (ersätter TopPerformingSalespeople)
  FunFacts: FunFacts;
}

// ---------- Request params ----------

export interface SalesManagerFilterParams {
  fromDate?: string;
  toDate?: string;
  userNos?: number[]; // ersätter tidigare salespersonId. Tom/undefined → alla säljare.
  state?: WorkItemStateEnum; // stöds av salesperson-stats och sales-team-summary
}
